import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import SchemaDetailContext from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  SCHEMA_UPDATE,
} from '@atom/graph/schema';
import { Progress } from '@atom/mui';
import { SchemaBudget, SchemaBudgetType } from '@atom/types/schema';

import ConversionUnitsSection from './ConversionUnitsSection';
import CostSection from './CostSection';
import ReadingsSection from './ReadingsSection';
import StockSection from './StockSection';

import classes from './costDetail.module.css';

const CostDetail = () => {
  const { schemaTree, refetchSchemaTree } = useContext(SchemaDetailContext);

  const [budget, setBudget] = useState<SchemaBudget>(schemaTree?.budget);

  useEffect(() => {
    setBudget(schemaTree?.budget);
  }, [schemaTree?.budget]);

  const [updateSchema, { loading: loadingUpdate }] = useMutation(SCHEMA_UPDATE);

  const [deleteConversionUnits, { loading: loadingDelete }] = useMutation(
    SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  );

  const updateBudget = async (updatedBudget: SchemaBudget) => {
    await updateSchema({
      variables: {
        input: {
          schemaId: schemaTree?.id,
          // conversion units have their own mutations
          budget: R.omit(['conversionUnits'], updatedBudget),
        },
      },
    });

    refetchSchemaTree();
  };

  const savedConversionUnits = schemaTree?.budget?.conversionUnits ?? [];

  // conversion units are relative to the cost type and base unit, so changing
  // either removes them. Only draft schemas can change those.
  const clearConversionUnits = async () => {
    await deleteConversionUnits({
      variables: {
        input: {
          schemaId: schemaTree?.id,
          conversionUnitIds: savedConversionUnits.map(({ id }) => id),
        },
      },
    });
  };

  const showConversionUnits = [
    SchemaBudgetType.VARIABLE_COST,
    SchemaBudgetType.FIXED,
  ].includes(budget.type);

  const costSection = (
    <CostSection
      budget={budget}
      setBudget={setBudget}
      updateBudget={updateBudget}
      hasConversionUnits={savedConversionUnits.length > 0}
      clearConversionUnits={clearConversionUnits}
    />
  );

  return (
    <div className={classes.centerPaneContainer}>
      <div className={classes.centerPane}>
        <div className={classes.costTitle}>
          <div>Cost Details</div>
          {(loadingUpdate || loadingDelete) && (
            <Progress size={25} thickness={2} />
          )}
        </div>
        <StockSection
          budget={budget}
          setBudget={setBudget}
          updateBudget={updateBudget}
        />
        <ReadingsSection
          budget={budget}
          setBudget={setBudget}
          updateBudget={updateBudget}
        />
        {costSection}
        {showConversionUnits && (
          <ConversionUnitsSection baseUnit={budget.unit} />
        )}
      </div>
    </div>
  );
};

export default CostDetail;
